import { z } from 'zod';

export const CURRENT_PROJECT_VERSION = 1;

export const ColumnSchema = z.object({
  name: z.string().min(1),
  physicalType: z.string().default('VARCHAR'),
  semanticType: z.enum(['dimension', 'measure', 'date', 'geography', 'identifier']).default('dimension'),
  nullable: z.boolean().default(true),
  displayName: z.string().optional(),
  description: z.string().optional(),
  format: z.string().optional(),
  hidden: z.boolean().default(false),
});

export const DataSourceSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  tableName: z.string().min(1),
  fileName: z.string().min(1),
  format: z.enum(['csv', 'tsv', 'json', 'ndjson', 'parquet', 'arrow', 'xlsx', 'geojson', 'view']),
  size: z.number().nonnegative(),
  storageKey: z.string().min(1),
  importedAt: z.string().datetime(),
  rowCount: z.number().nonnegative().default(0),
  columns: z.array(ColumnSchema).default([]),
});

export const TransformStepSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  kind: z.enum(['filter', 'select', 'rename', 'cast', 'derive', 'aggregate', 'join', 'union', 'pivot', 'unpivot', 'sql']),
  inputTable: z.string().min(1),
  outputTable: z.string().min(1),
  sql: z.string().min(1),
  enabled: z.boolean().default(true),
  createdAt: z.string().datetime(),
});

export const RelationshipSchema = z.object({
  id: z.string().min(1),
  fromTable: z.string().min(1),
  fromColumn: z.string().min(1),
  toTable: z.string().min(1),
  toColumn: z.string().min(1),
  cardinality: z.enum(['one-to-one', 'one-to-many', 'many-to-one', 'many-to-many']).default('many-to-one'),
  filterDirection: z.enum(['single', 'both']).default('single'),
  active: z.boolean().default(true),
});

export const MeasureSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  tableName: z.string().min(1),
  expression: z.string().min(1),
  format: z.string().default('#,##0.00'),
  description: z.string().optional(),
});

export const FilterSchema = z.object({
  field: z.string().min(1),
  operator: z.enum(['=', '!=', '>', '>=', '<', '<=', 'contains', 'in', 'is-null', 'is-not-null']),
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number(), z.boolean()]))]).optional(),
});

export const VisualSpecSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  tableName: z.string().min(1),
  chartType: z.enum(['table', 'kpi', 'bar', 'line', 'area', 'scatter', 'histogram', 'heatmap', 'boxplot', 'treemap', 'map']),
  dimension: z.string().optional(),
  measure: z.string().optional(),
  aggregation: z.enum(['sum', 'avg', 'min', 'max', 'count', 'count-distinct', 'none']).default('sum'),
  color: z.string().optional(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  filters: z.array(FilterSchema).default([]),
  limit: z.number().int().positive().max(100000).default(5000),
  renderer: z.enum(['vega', 'echarts', 'map']).default('vega'),
  createdAt: z.string().datetime(),
});

export const DashboardItemSchema = z.object({
  id: z.string().min(1),
  visualId: z.string().min(1),
  x: z.number().int().nonnegative(),
  y: z.number().int().nonnegative(),
  w: z.number().int().positive(),
  h: z.number().int().positive(),
});

export const DashboardPageSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  items: z.array(DashboardItemSchema).default([]),
});

export const NotebookCellSchema = z.object({
  id: z.string().min(1),
  type: z.enum(['markdown', 'sql', 'python']),
  source: z.string(),
  output: z.string().optional(),
});

export const PrivacyEventSchema = z.object({
  id: z.string().min(1),
  timestamp: z.string().datetime(),
  category: z.enum(['local', 'remote-source', 'model-download', 'map-tile', 'plugin']),
  destination: z.string().optional(),
  purpose: z.string().min(1),
  bytes: z.number().nonnegative().optional(),
});

export const ProjectSchema = z.object({
  format: z.literal('numbernode-project'),
  formatVersion: z.number().int().positive(),
  id: z.string().min(1),
  name: z.string().min(1),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  sources: z.array(DataSourceSchema).default([]),
  transforms: z.array(TransformStepSchema).default([]),
  relationships: z.array(RelationshipSchema).default([]),
  measures: z.array(MeasureSchema).default([]),
  visuals: z.array(VisualSpecSchema).default([]),
  dashboards: z.array(DashboardPageSchema).default([]),
  notebook: z.array(NotebookCellSchema).default([]),
  privacyLedger: z.array(PrivacyEventSchema).default([]),
  settings: z.object({
    localOnly: z.boolean().default(true),
    theme: z.enum(['system', 'light', 'dark']).default('system'),
  }).default({localOnly: true, theme: 'system'}),
});

export type ColumnDefinition = z.infer<typeof ColumnSchema>;
export type DataSource = z.infer<typeof DataSourceSchema>;
export type TransformStep = z.infer<typeof TransformStepSchema>;
export type Relationship = z.infer<typeof RelationshipSchema>;
export type Measure = z.infer<typeof MeasureSchema>;
export type Filter = z.infer<typeof FilterSchema>;
export type VisualSpec = z.infer<typeof VisualSpecSchema>;
export type DashboardPage = z.infer<typeof DashboardPageSchema>;
export type NotebookCell = z.infer<typeof NotebookCellSchema>;
export type PrivacyEvent = z.infer<typeof PrivacyEventSchema>;
export type NumberNodeProject = z.infer<typeof ProjectSchema>;

export function newId(prefix = 'id'): string {
  return `${prefix}_${crypto.randomUUID()}`;
}

export function createEmptyProject(name = 'Untitled analysis'): NumberNodeProject {
  const now = new Date().toISOString();
  return ProjectSchema.parse({
    format: 'numbernode-project',
    formatVersion: CURRENT_PROJECT_VERSION,
    id: newId('project'),
    name,
    createdAt: now,
    updatedAt: now,
    sources: [],
    transforms: [],
    relationships: [],
    measures: [],
    visuals: [],
    dashboards: [{ id: newId('page'), name: 'Dashboard 1', items: [] }],
    notebook: [],
    privacyLedger: [{
      id: newId('privacy'),
      timestamp: now,
      category: 'local',
      purpose: 'Project created locally',
    }],
    settings: { localOnly: true, theme: 'system' },
  });
}

export function migrateProject(input: unknown): NumberNodeProject {
  const raw = z.record(z.string(), z.unknown()).parse(input);
  const version = Number(raw.formatVersion ?? 1);
  if (version > CURRENT_PROJECT_VERSION) {
    throw new Error(`This project requires a newer NumberNode version (format ${version}).`);
  }
  return ProjectSchema.parse({ ...raw, formatVersion: CURRENT_PROJECT_VERSION });
}
